import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Row, Col, Table, Form, Modal, Badge } from 'react-bootstrap';
import { SpacingRow, StyledTable, StyledTableHeader, TableContainer, StyledContainer, CreateButton, PrimaryButton, SecondaryButton, WarningButton, DangerButton, InfoButton } from './SchedulesManage.styled';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { classActions } from '../../redux/actions/class.actions';
import { scheduleActions, studentActions, userActions } from '../../redux/actions';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import { FiUser, FiClock, FiCalendar, FiCheckCircle, FiEdit2, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import holidayDateService from '../../services/holidayDate.service';
import classMonthlySessionService from '../../services/classMonthlySession.service';
import ScheduleCalendarView from './ScheduleCalendarView';

interface Schedule {
  id?: number;
  classId: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

interface ClassSchedule {
  className: string;
  schedules: Schedule[];
}
interface ClassItem {
  id: number;
  name: string;
  teacherId: number;
  sessionFee: number;
  monthlyFee: number | null;
  note: string;
  createdAt: string;
  updatedAt: string;
}

interface HolidayDate {
  id: number;
  date: string;
  endDate?: string;   // nếu có = khoảng nghỉ
  name: string;
}

interface ClassMonthlySession {
  id: number;
  classId: number;
  sessionDate: string;
  startTime: string;
  endTime: string;
  month: number;
  year: number;
  isActive: boolean;
  source: 'schedule' | 'manual';
  note?: string;
  confirmedAt?: string;
}

const ScheduleManage = () => {
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [selectedClass, setSelectedClass] = useState<string>('Tất cả');
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>(''); // '' = tất cả giáo viên
  const [selectedDays, setSelectedDays] = useState<number[]>([]); // Filter theo thứ trong tuần
  const [copiedSchedule, setCopiedSchedule] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [classId, setClassId] = useState<string>('');
  const [editingClassName, setEditingClassName] = useState<string>('');
  const [numSessions, setNumSessions] = useState<number>(1);
  const [schedules, setSchedules] = useState<{ dayOfWeek: number; startTime: string; endTime: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletingClass, setDeletingClass] = useState<{ classId: number, className: string } | null>(null);

  // ── Holiday state ──
  const [holidays, setHolidays] = useState<HolidayDate[]>([]);
  const [showHolidayPanel, setShowHolidayPanel] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ date: '', endDate: '', name: '' });
  const [editingHoliday, setEditingHoliday] = useState<HolidayDate | null>(null);
  const [holidayLoading, setHolidayLoading] = useState(false);

  // ── Monthly sessions state ──
  // confirmedMap: classId → list of confirmed sessions
  const [confirmedMap, setConfirmedMap] = useState<Record<number, ClassMonthlySession[]>>({});
  const [confirmingClassId, setConfirmingClassId] = useState<number | null>(null);
  // Edit sessions modal
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [editingSessionsClassId, setEditingSessionsClassId] = useState<number | null>(null);
  const [editingSessionsClassName, setEditingSessionsClassName] = useState('');
  const [editingSessions, setEditingSessions] = useState<ClassMonthlySession[]>([]);
  const [addingManualSession, setAddingManualSession] = useState(false);
  const [manualSessionForm, setManualSessionForm] = useState({ sessionDate: '', startTime: '', endTime: '', note: '' });

  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const classes = useAppSelector((state: any) => state.classes.classesList);
  const schedulesList = useAppSelector((state: any) => state.schedules.schedulesList);
  const students = useAppSelector((state: any) => state.students.studentsList);
  const teachers = useAppSelector((state: any) => state.users.items || []);
  const dispatch = useAppDispatch();

  const classIdsWithSchedule = new Set(schedulesList.map((schedule: Schedule) => schedule.classId));
  const classesWithoutSchedule = classes.filter((c: ClassItem) => !classIdsWithSchedule.has(c.id));

  const handleClassSelect = (className: string) => { setSelectedClass(className); };
  const handleClickDelete = (classId: number, className: string) => {
    setDeletingClass({ classId, className });
    setShowDeletePopup(true);
  };
  const handleConfirmDelete = () => {
    if (deletingClass) {
      dispatch(scheduleActions.deleteSchedule(deletingClass.classId));
      setShowDeletePopup(false);
      setDeletingClass(null);
    }
  };

  const schedulesData: ClassSchedule[] = classes
    .map((classItem: ClassItem) => {
      const scheds = schedulesList
        .filter((schedule: Schedule) => schedule.classId === classItem.id)
        .map((schedule: Schedule) => ({ id: schedule.id, dayOfWeek: schedule.dayOfWeek, startTime: schedule.startTime, endTime: schedule.endTime, classId: schedule.classId }))
        .sort((a: any, b: any) => Number(a.dayOfWeek) - Number(b.dayOfWeek));
      return scheds.length > 0 ? { className: classItem.name, schedules: scheds } : null;
    })
    .filter((item: ClassSchedule): item is ClassSchedule => item !== null);

  const convertDayOfWeekToVietnamese = (dayOfWeek: number) => {
    const days = ['', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];
    return days[dayOfWeek - 1];
  };

  const convertTimeToVietnamese = (start: string, end: string) => {
    if (!start || !end) return '';
    const formatTime = (time: string) => {
      const [hourStr, minuteStr] = time.split(':');
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (isNaN(hour) || isNaN(minute)) return '';
      const formattedHour = hour < 12 ? hour : hour - 12;
      return minute === 0 ? `${formattedHour}h` : `${formattedHour}h${minute < 10 ? `0${minute}` : minute}`;
    };
    return `${formatTime(start)} -> ${formatTime(end)}`;
  };

  const formatTimeForInput = (time: string) => {
    if (!time) return '';
    return time.length > 5 ? time.slice(0, 5) : time;
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(classActions.getAll()),
      dispatch(scheduleActions.getAll()),
      dispatch(userActions.getAllTeachers()),
      dispatch(studentActions.getAll()),
    ]).then(() => setIsLoading(false)).catch(() => setIsLoading(false));
  }, [dispatch]);

  // Load holidays & confirmed sessions whenever month/year changes
  const loadHolidaysAndSessions = useCallback(async () => {
    try {
      const [hRes] = await Promise.all([
        holidayDateService.getAll(currentMonth, currentYear),
      ]);
      setHolidays(hRes.data || []);
    } catch { /* ignore */ }
  }, [currentMonth, currentYear]);

  useEffect(() => { loadHolidaysAndSessions(); }, [loadHolidaysAndSessions]);

  // Load confirmed sessions for all classes for current month/year
  const loadConfirmedSessions = useCallback(async () => {
    try {
      const res = await classMonthlySessionService.getAll({ month: currentMonth, year: currentYear });
      const sessions: ClassMonthlySession[] = res.data || [];
      const map: Record<number, ClassMonthlySession[]> = {};
      sessions.forEach(s => {
        if (!map[s.classId]) map[s.classId] = [];
        map[s.classId].push(s);
      });
      setConfirmedMap(map);
    } catch { /* ignore */ }
  }, [currentMonth, currentYear]);

  useEffect(() => { loadConfirmedSessions(); }, [loadConfirmedSessions]);

  // ── Holiday helpers ──
  // Format ngày nghỉ lễ dạng DD/MM (dùng / thay vì - của toLocaleDateString)
  const fmtHolidayDate = (dateStr: string): string => {
    const [, m, d] = dateStr.split('T')[0].split('-');
    return `${d}/${m}`;
  };

  // Expand holiday dates/ranges vào tập ngày YYYY-MM-DD → tên
  const buildHolidayMap = (): Map<string, string> => {
    const map = new Map<string, string>();
    holidays.forEach(h => {
      const start = new Date(h.date);
      const end = h.endDate ? new Date(h.endDate) : start;
      const cur = new Date(start);
      while (cur <= end) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
        map.set(ds, h.name);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  };

  const getMonthSchedule = (schedules: Schedule[], month: number, year: number): string => {
    const holidayMap = buildHolidayMap();

    const daysInMonth = new Date(year, month, 0).getDate();
    let active: { day: number; label: string }[] = [];
    let skipped: { day: number; label: string; holidayName: string }[] = [];

    schedules.forEach((schedule) => {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const jsDay = date.getDay();
        const convertedDay = jsDay === 0 ? 8 : jsDay + 1;
        if (schedule.dayOfWeek === convertedDay) {
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const holidayName = holidayMap.get(dateStr);
          if (holidayName) {
            skipped.push({ day, label: `${convertDayOfWeekToVietnamese(schedule.dayOfWeek)} ${day}/${month}`, holidayName });
          } else {
            active.push({ day, label: `${convertDayOfWeekToVietnamese(schedule.dayOfWeek)} ${day}/${month}` });
          }
        }
      }
    });

    active.sort((a, b) => a.day - b.day);
    skipped.sort((a, b) => a.day - b.day);

    let result = `${active.length} buổi: ${active.map(s => s.label).join(', ')}`;
    if (skipped.length > 0) {
      result += ` | ⛔ Nghỉ lễ (${skipped.length}): ${skipped.map(s => `${s.label} (${s.holidayName})`).join(', ')}`;
    }
    return result;
  };

  const sortedSchedules = schedulesData
    .filter((schedule) => {
      // Filter theo lớp
      if (selectedClass !== 'Tất cả' && schedule.className !== selectedClass) return false;
      // Filter theo giáo viên
      if (selectedTeacherId) {
        const classItem = classes.find((c: ClassItem) => c.name === schedule.className);
        if (!classItem || String(classItem.teacherId) !== selectedTeacherId) return false;
      }
      // Filter theo thứ: lớp phải có ít nhất 1 buổi học vào 1 trong các thứ đã chọn
      if (selectedDays.length > 0) {
        const classDays = schedule.schedules.map((s: Schedule) => s.dayOfWeek);
        return selectedDays.some(d => classDays.includes(d));
      }
      return true;
    })
    .sort((a, b) => a.className.localeCompare(b.className));

  const toggleDay = (day: number) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const availableClasses = modalMode === 'edit' ? classes : classesWithoutSchedule;

  const handleCopySchedule = (scheduleText: string) => {
    setCopiedSchedule(scheduleText);
    navigator.clipboard.writeText(scheduleText);
  };

  const handleOpenCreateModal = () => { setModalMode('create'); setShowModal(true); setClassId(''); setEditingClassName(''); setNumSessions(1); setSchedules([]); };
  const handleOpenEditModal = (targetClassId: number, className: string, existingSchedules: Schedule[]) => {
    setModalMode('edit'); setShowModal(true); setClassId(String(targetClassId)); setEditingClassName(className);
    const normalized = existingSchedules.map((s) => ({ id: s.id, dayOfWeek: s.dayOfWeek, startTime: formatTimeForInput(s.startTime), endTime: formatTimeForInput(s.endTime) }));
    setSchedules(normalized); setNumSessions(normalized.length || 1);
  };
  const handleCloseModal = () => { setShowModal(false); setModalMode('create'); setClassId(''); setEditingClassName(''); setNumSessions(1); setSchedules([]); };
  const handleSaveSchedule = () => {
    const payload = { classId: Number(classId), schedules };
    if (modalMode === 'edit') dispatch(scheduleActions.updateSchedule(payload));
    else dispatch(scheduleActions.createSchedule(payload));
    handleCloseModal();
  };

  const getSessionLabel = (start: string): string => {
    const [hourStr] = start.split(':');
    const hour = parseInt(hourStr, 10);
    if (isNaN(hour)) return '';
    if (hour < 12) return 'Sáng';
    if (hour < 18) return 'Chiều';
    return 'Tối';
  };

  const joinWithAnd = (items: string[]) => {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} và ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} và ${items[items.length - 1]}`;
  };

  const groupSchedules = (schedules: Schedule[]) => {
    const map = new Map<string, { days: number[]; startTime: string; endTime: string; session: string }>();
    schedules.forEach((s) => {
      const session = getSessionLabel(s.startTime);
      const key = `${session}__${s.startTime}__${s.endTime}`;
      if (!map.has(key)) map.set(key, { days: [], startTime: s.startTime, endTime: s.endTime, session });
      const entry = map.get(key)!;
      if (!entry.days.includes(s.dayOfWeek)) entry.days.push(s.dayOfWeek);
    });
    const groups = Array.from(map.values()).map((g) => { g.days.sort((a, b) => Number(a) - Number(b)); return g; });
    groups.sort((a, b) => a.days[0] - b.days[0]);
    return groups;
  };

  const getStudentCounts = (classId: number) => {
    const classStudents = students.filter((s: any) => s.classId === classId);
    const activeCount = classStudents.filter((s: any) => s.startDate !== null).length;
    const pendingCount = classStudents.filter((s: any) => s.startDate === null).length;
    return { activeCount, pendingCount };
  };

  // ── Holiday CRUD ──
  const handleSaveHoliday = async () => {
    if (!holidayForm.date) {
      alert('⚠️ Vui lòng chọn ngày bắt đầu nghỉ lễ!');
      return;
    }
    if (!holidayForm.name.trim()) {
      alert('⚠️ Vui lòng nhập tên khoảng nghỉ!');
      return;
    }
    // Validate: nếu có endDate phải >= date
    if (holidayForm.endDate && holidayForm.endDate < holidayForm.date) {
      alert('Ngày kết thúc phải >= ngày bắt đầu!');
      return;
    }
    setHolidayLoading(true);
    try {
      const payload = {
        date: holidayForm.date,
        endDate: holidayForm.endDate || undefined,
        name: holidayForm.name.trim(),
      };
      console.log('[Holiday] Saving payload:', payload, 'editing:', editingHoliday?.id);
      if (editingHoliday) {
        await holidayDateService.update(editingHoliday.id, payload);
      } else {
        await holidayDateService.create(payload);
      }
      setHolidayForm({ date: '', endDate: '', name: '' });
      setEditingHoliday(null);
      await loadHolidaysAndSessions();
      console.log('[Holiday] Saved OK');
    } catch (e: any) {
      console.error('[Holiday] Save error:', e);
      alert('❌ Lỗi khi lưu ngày nghỉ lễ: ' + (e?.message || e));
    }
    finally { setHolidayLoading(false); }
  };

  const handleDeleteHoliday = async (id: number) => {
    if (!window.confirm('Xóa ngày nghỉ này?')) return;
    try {
      await holidayDateService.delete(id);
      await loadHolidaysAndSessions();
    } catch (e: any) { alert('Lỗi: ' + e); }
  };

  // ── Confirm sessions for a class ──
  const handleConfirmSessions = async (cId: number, className: string) => {
    if (!window.confirm(`Xác nhận và lưu buổi học tháng ${currentMonth}/${currentYear} cho lớp ${className}?\n(Sẽ tự trừ các ngày nghỉ lễ)`)) return;
    setConfirmingClassId(cId);
    try {
      const res = await classMonthlySessionService.confirm(cId, currentMonth, currentYear);
      alert(res.message || 'Đã xác nhận!');
      await loadConfirmedSessions();
    } catch (e: any) { alert('Lỗi: ' + e); }
    finally { setConfirmingClassId(null); }
  };

  // ── Edit sessions modal ──
  const handleOpenSessionsModal = async (cId: number, className: string) => {
    setEditingSessionsClassId(cId);
    setEditingSessionsClassName(className);
    try {
      const res = await classMonthlySessionService.getAll({ classId: cId, month: currentMonth, year: currentYear });
      setEditingSessions(res.data || []);
    } catch { setEditingSessions([]); }
    setShowSessionsModal(true);
  };

  const handleToggleSession = async (session: ClassMonthlySession) => {
    const wasActive = session.isActive;

    // Always cross-check the session date with the current holidays map
    // This is reliable even after multiple toggles (don't trust note string)
    const holidayMap = buildHolidayMap();
    const holidayName = holidayMap.get(session.sessionDate) ?? '';
    const isHolidayDate = holidayName !== '';

    let newNote: string;
    if (wasActive) {
      // Going Học → Nghỉ
      newNote = isHolidayDate ? `HL:${holidayName}` : 'ADMIN_OFF';
    } else {
      // Going Nghỉ → Học
      newNote = isHolidayDate ? `ACTIVE_HL:${holidayName}` : '';
    }

    try {
      await classMonthlySessionService.update(session.id, { isActive: !wasActive, note: newNote });
      setEditingSessions(prev => prev.map(s =>
        s.id === session.id ? { ...s, isActive: !wasActive, note: newNote } : s
      ));
      await loadConfirmedSessions();
    } catch (e: any) { alert('Lỗi: ' + e); }
  };

  const handleDeleteSession = async (id: number) => {
    if (!window.confirm('Xóa buổi học này?')) return;
    try {
      await classMonthlySessionService.delete(id);
      setEditingSessions(prev => prev.filter(s => s.id !== id));
      await loadConfirmedSessions();
    } catch (e: any) { alert('Lỗi: ' + e); }
  };

  const handleAddManualSession = async () => {
    if (!manualSessionForm.sessionDate || !manualSessionForm.startTime || !manualSessionForm.endTime) {
      alert('Vui lòng điền đầy đủ ngày, giờ bắt đầu và giờ kết thúc!');
      return;
    }
    if (editingSessionsClassId == null) return;
    const d = new Date(manualSessionForm.sessionDate);
    try {
      await classMonthlySessionService.addManual({
        classId: editingSessionsClassId,
        sessionDate: manualSessionForm.sessionDate,
        startTime: manualSessionForm.startTime,
        endTime: manualSessionForm.endTime,
        month: d.getMonth() + 1,
        year: d.getFullYear(),
        note: manualSessionForm.note || 'Buổi bù',
      });
      setManualSessionForm({ sessionDate: '', startTime: '', endTime: '', note: '' });
      setAddingManualSession(false);
      const res = await classMonthlySessionService.getAll({ classId: editingSessionsClassId, month: currentMonth, year: currentYear });
      setEditingSessions(res.data || []);
      await loadConfirmedSessions();
    } catch (e: any) { alert('Lỗi: ' + e); }
  };

  const getConfirmedCount = (cId: number) => {
    const sessions = confirmedMap[cId] || [];
    return sessions.filter(s => s.isActive).length;
  };

  const isConfirmed = (cId: number) => (confirmedMap[cId] || []).length > 0;

  // Handler cho calendar view khi kéo thả
  const handleCalendarUpdateSchedule = useCallback((classId: number, updatedSchedules: any[]) => {
    dispatch(scheduleActions.updateSchedule({ classId, schedules: updatedSchedules }));
  }, [dispatch]);

  return (
    <StyledContainer>
      <SpacingRow style={{ alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
        <Col xs="auto">
          <CreateButton onClick={handleOpenCreateModal}>Tạo lịch học</CreateButton>
        </Col>
        {/* Toggle table / calendar */}
        <Col xs="auto">
          <div style={{ display: 'flex', background: '#f0f2f5', borderRadius: 10, padding: 3, gap: 2 }}>
            <button
              onClick={() => setViewMode('table')}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'table' ? 'white' : 'transparent',
                color: viewMode === 'table' ? '#1976d2' : '#888',
                boxShadow: viewMode === 'table' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              📋 Bảng
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer',
                background: viewMode === 'calendar' ? 'white' : 'transparent',
                color: viewMode === 'calendar' ? '#7b1fa2' : '#888',
                boxShadow: viewMode === 'calendar' ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              📅 Lịch
            </button>
          </div>
        </Col>
        {/* Filter lớp */}
        <Col xs={3} style={{ minWidth: '140px', maxWidth: '200px' }}>
          <Form.Group controlId="classSelect" style={{ marginBottom: 0 }}>
            <Form.Control as="select" value={selectedClass} onChange={(e) => handleClassSelect(e.target.value)} style={{ fontSize: '0.88rem' }}>
              <option value="Tất cả">Tất cả lớp</option>
              {[...schedulesData].sort((a, b) => a.className.localeCompare(b.className)).map((schedule, index) => (
                <option key={index} value={schedule.className}>{schedule.className}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        {/* Filter tháng */}
        <Col xs={2} style={{ minWidth: '110px', maxWidth: '140px' }}>
          <Form.Group controlId="monthSelect" style={{ marginBottom: 0 }}>
            <Form.Control as="select" value={currentMonth} onChange={(e) => setCurrentMonth(parseInt(e.target.value))} style={{ fontSize: '0.88rem' }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>Tháng {m}</option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        {/* Filter năm */}
        <Col xs={2} style={{ minWidth: '90px', maxWidth: '120px' }}>
          <Form.Group controlId="yearSelect" style={{ marginBottom: 0 }}>
            <Form.Control as="select" value={currentYear} onChange={(e) => setCurrentYear(parseInt(e.target.value))} style={{ fontSize: '0.88rem' }}>
              {[2024, 2025, 2026, 2027].map(y => (<option key={y} value={y}>{y}</option>))}
            </Form.Control>
          </Form.Group>
        </Col>
        {/* Nút quản lý nghỉ lễ — chỉ admin */}
        {userInfo?.role === 0 && (
          <Col xs="auto">
            <InfoButton onClick={() => setShowHolidayPanel(v => !v)} style={{ fontSize: '0.85rem' }}>
              🎌 Nghỉ lễ T{currentMonth}/{currentYear} {holidays.length > 0 && <Badge bg="danger" style={{ marginLeft: 4 }}>{holidays.length}</Badge>}
            </InfoButton>
          </Col>
        )}
      </SpacingRow>

      {/* ── Filter theo thứ trong tuần (tag style) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '10px 0 4px 0' }}>
        <span style={{ fontSize: '0.82rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>📅 Lọc theo thứ:</span>
        {[
          { day: 2, label: 'Thứ 2' },
          { day: 3, label: 'Thứ 3' },
          { day: 4, label: 'Thứ 4' },
          { day: 5, label: 'Thứ 5' },
          { day: 6, label: 'Thứ 6' },
          { day: 7, label: 'Thứ 7' },
          { day: 8, label: 'CN' },
        ].map(({ day, label }) => {
          const active = selectedDays.includes(day);
          // Đếm bao nhiêu lớp có buổi học vào thứ này
          const count = schedulesData.filter(s => s.schedules.some((sc: Schedule) => sc.dayOfWeek === day)).length;
          return (
            <button
              key={day}
              onClick={() => toggleDay(day)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                border: active ? '2px solid #1976d2' : '1.5px solid #bdbdbd',
                borderRadius: 20,
                padding: '3px 12px',
                fontSize: '0.82rem',
                fontWeight: active ? 700 : 400,
                cursor: 'pointer',
                background: active ? '#e3f2fd' : '#fff',
                color: active ? '#1565c0' : '#555',
                boxShadow: active ? '0 0 0 2px #90caf9' : 'none',
                transition: 'all 0.18s',
                userSelect: 'none',
              }}
            >
              {label}
              {count > 0 && (
                <span style={{
                  background: active ? '#1976d2' : '#e0e0e0',
                  color: active ? '#fff' : '#666',
                  borderRadius: 10,
                  padding: '0 6px',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  minWidth: 18,
                  textAlign: 'center',
                }}>{count}</span>
              )}
            </button>
          );
        })}
        {selectedDays.length > 0 && (
          <button
            onClick={() => setSelectedDays([])}
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: '#e53935',
              fontSize: '0.8rem',
              padding: '3px 6px',
              fontWeight: 600,
            }}
          >
            ✕ Bỏ lọc
          </button>
        )}
        {selectedDays.length > 0 && (
          <span style={{ fontSize: '0.8rem', color: '#1976d2', fontStyle: 'italic' }}>
            → Tìm thấy {sortedSchedules.length} lớp
          </span>
        )}
      </div>

      {/* ── Filter theo giáo viên (tag style) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '4px 0 8px 0' }}>
        <span style={{ fontSize: '0.82rem', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' }}>👨‍🏫 GV:</span>
        {/* Tag "Tất cả" */}
        <button
          onClick={() => setSelectedTeacherId('')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            border: selectedTeacherId === '' ? '2px solid #7b1fa2' : '1.5px solid #bdbdbd',
            borderRadius: 20, padding: '3px 12px',
            fontSize: '0.82rem', fontWeight: selectedTeacherId === '' ? 700 : 400,
            cursor: 'pointer',
            background: selectedTeacherId === '' ? '#f3e5f5' : '#fff',
            color: selectedTeacherId === '' ? '#6a1b9a' : '#555',
            boxShadow: selectedTeacherId === '' ? '0 0 0 2px #ce93d8' : 'none',
            transition: 'all 0.18s', userSelect: 'none',
          }}
        >
          Tất cả
        </button>
        {/* Tags từng giáo viên */}
        {[...teachers]
          .filter((t: any) => t.role === 1 || t.role === 0)
          .sort((a: any, b: any) => (a.userName || '').localeCompare(b.userName || ''))
          .map((t: any) => {
            const active = selectedTeacherId === String(t.id);
            // Đếm số lớp của giáo viên này có trong schedulesData
            const count = schedulesData.filter(sch => {
              const ci = classes.find((c: ClassItem) => c.name === sch.className);
              return ci && String(ci.teacherId) === String(t.id);
            }).length;
            if (count === 0) return null; // Ẩn giáo viên không có lớp nào
            return (
              <button
                key={t.id}
                onClick={() => setSelectedTeacherId(String(t.id))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  border: active ? '2px solid #7b1fa2' : '1.5px solid #bdbdbd',
                  borderRadius: 20, padding: '3px 12px',
                  fontSize: '0.82rem', fontWeight: active ? 700 : 400,
                  cursor: 'pointer',
                  background: active ? '#f3e5f5' : '#fff',
                  color: active ? '#6a1b9a' : '#555',
                  boxShadow: active ? '0 0 0 2px #ce93d8' : 'none',
                  transition: 'all 0.18s', userSelect: 'none',
                }}
              >
                {t.userName}
                <span style={{
                  background: active ? '#7b1fa2' : '#e0e0e0',
                  color: active ? '#fff' : '#666',
                  borderRadius: 10, padding: '0 6px', fontSize: '0.72rem', fontWeight: 700,
                  minWidth: 18, textAlign: 'center',
                }}>{count}</span>
              </button>
            );
          })}
      </div>

      {/* ── Panel quản lý ngày nghỉ lễ — chỉ admin ── */}
      {userInfo?.role === 0 && showHolidayPanel && (
        <div style={{ background: '#fff8e1', border: '1.5px solid #fbc02d', borderRadius: 10, padding: '16px 20px', margin: '12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <strong>🎌 Ngày nghỉ lễ tháng {currentMonth}/{currentYear}</strong>
            <FiX style={{ cursor: 'pointer', fontSize: 18 }} onClick={() => setShowHolidayPanel(false)} />
          </div>
          {/* Danh sách ngày nghỉ */}
          {holidays.length === 0 ? (
            <p style={{ color: '#888', fontSize: '0.88rem' }}>Chưa có ngày nghỉ lễ nào trong tháng này.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {holidays.map(h => (
                <div key={h.id} style={{ background: '#fff', border: `1px solid ${h.endDate ? '#7b1fa2' : '#fbc02d'}`, borderRadius: 8, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                  {h.endDate ? (
                    <strong style={{ color: '#7b1fa2' }}>🗓 {fmtHolidayDate(h.date)} → {fmtHolidayDate(h.endDate)}</strong>
                  ) : (
                    <strong>{fmtHolidayDate(h.date)}</strong>
                  )}
                  — {h.name}
                  <FiEdit2 style={{ cursor: 'pointer', color: '#1976d2' }} onClick={() => { setEditingHoliday(h); setHolidayForm({ date: h.date.split('T')[0], endDate: h.endDate ? h.endDate.split('T')[0] : '', name: h.name }); }} />
                  <FiTrash2 style={{ cursor: 'pointer', color: '#c62828' }} onClick={() => handleDeleteHoliday(h.id)} />
                </div>
              ))}
            </div>
          )}
          {/* Form thêm/sửa ngày nghỉ */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <small style={{ color: '#666', fontSize: '0.75rem' }}>Từ ngày *</small>
              <Form.Control type="date" value={holidayForm.date} onChange={e => setHolidayForm(f => ({ ...f, date: e.target.value }))} style={{ maxWidth: 150, fontSize: '0.88rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <small style={{ color: '#666', fontSize: '0.75rem' }}>Chọn ngày (rồi đến ngày) — để trống nếu 1 ngày</small>
              <Form.Control type="date" value={holidayForm.endDate} onChange={e => setHolidayForm(f => ({ ...f, endDate: e.target.value }))} style={{ maxWidth: 150, fontSize: '0.88rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <small style={{ color: '#666', fontSize: '0.75rem' }}>Tên khoảng nghỉ *</small>
              <Form.Control type="text" placeholder="VD: Nghỉ Tết 2026" value={holidayForm.name} onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))} style={{ maxWidth: 280, fontSize: '0.88rem' }} />
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', paddingBottom: 0, marginTop: 18 }}>
              <PrimaryButton onClick={handleSaveHoliday} disabled={holidayLoading} style={{ fontSize: '0.85rem', padding: '6px 14px' }}>
                {editingHoliday ? '💾 Cập nhật' : '➕ Thêm'}
              </PrimaryButton>
              {editingHoliday && (
                <SecondaryButton onClick={() => { setEditingHoliday(null); setHolidayForm({ date: '', endDate: '', name: '' }); }} style={{ fontSize: '0.85rem', padding: '6px 14px' }}>Hủy</SecondaryButton>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule Create/Edit Modal ── */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'edit' ? 'Sửa lịch học' : 'Tạo lịch học'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Lớp</Form.Label>
              <Form.Control as="select" value={classId} onChange={(e) => setClassId(e.target.value)} disabled={modalMode === 'edit'}>
                <option value="">Chọn lớp</option>
                {availableClasses.map((c: any) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </Form.Control>
              {modalMode === 'edit' && editingClassName && (<div style={{ marginTop: 6, fontStyle: 'italic', color: '#555' }}>{editingClassName}</div>)}
            </Form.Group>
            <Form.Group>
              <Form.Label>Số buổi/tuần</Form.Label>
              <Form.Control as="select" value={numSessions} onChange={(e) => {
                const nextValue = Number(e.target.value);
                setNumSessions(nextValue);
                setSchedules((prev) => {
                  const next = [...prev];
                  while (next.length < nextValue) next.push({ dayOfWeek: 2, startTime: '', endTime: '' });
                  return next.slice(0, nextValue);
                });
              }}>
                {[...Array(7)].map((_, i) => (<option key={i + 1} value={i + 1}>{i + 1}</option>))}
              </Form.Control>
            </Form.Group>
            {Array.from({ length: numSessions }).map((_, index) => (
              <Row key={index}>
                <Col>
                  <Form.Group>
                    <Form.Label>Thứ</Form.Label>
                    <Form.Control as="select" value={schedules[index]?.dayOfWeek ?? 2} onChange={(e) => {
                      const value = Number(e.target.value);
                      const newSchedules = [...schedules];
                      const current = newSchedules[index] ?? { dayOfWeek: 2, startTime: '', endTime: '' };
                      newSchedules[index] = { ...current, dayOfWeek: value };
                      setSchedules(newSchedules);
                    }}>
                      {[2, 3, 4, 5, 6, 7, 8].map((d) => (<option key={d} value={d}>Thứ {d === 8 ? 'Chủ Nhật' : d}</option>))}
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Từ</Form.Label>
                    <Form.Control type="time" value={schedules[index]?.startTime ?? ''} onChange={(e) => {
                      const ns = [...schedules]; const current = ns[index] ?? { dayOfWeek: 2, startTime: '', endTime: '' };
                      ns[index] = { ...current, startTime: e.target.value }; setSchedules(ns);
                    }} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Đến</Form.Label>
                    <Form.Control type="time" value={schedules[index]?.endTime ?? ''} onChange={(e) => {
                      const ns = [...schedules]; const current = ns[index] ?? { dayOfWeek: 2, startTime: '', endTime: '' };
                      ns[index] = { ...current, endTime: e.target.value }; setSchedules(ns);
                    }} />
                  </Form.Group>
                </Col>
              </Row>
            ))}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <SecondaryButton onClick={handleCloseModal}>Hủy</SecondaryButton>
          <PrimaryButton onClick={handleSaveSchedule}>{modalMode === 'edit' ? 'Lưu' : 'Tạo'}</PrimaryButton>
        </Modal.Footer>
      </Modal>

      {/* ── Sessions Edit Modal ── */}
      <Modal show={showSessionsModal} onHide={() => setShowSessionsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>📅 Buổi học T{currentMonth}/{currentYear} — {editingSessionsClassName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <InfoButton onClick={() => setAddingManualSession(v => !v)} style={{ fontSize: '0.83rem' }}>
              <FiPlus /> Thêm buổi bù
            </InfoButton>
          </div>
          {addingManualSession && (
            <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '12px 16px', marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Form.Control type="date" value={manualSessionForm.sessionDate} onChange={e => setManualSessionForm(f => ({ ...f, sessionDate: e.target.value }))} style={{ maxWidth: 150 }} />
              <Form.Control type="time" value={manualSessionForm.startTime} onChange={e => setManualSessionForm(f => ({ ...f, startTime: e.target.value }))} style={{ maxWidth: 120 }} />
              <span>→</span>
              <Form.Control type="time" value={manualSessionForm.endTime} onChange={e => setManualSessionForm(f => ({ ...f, endTime: e.target.value }))} style={{ maxWidth: 120 }} />
              <Form.Control type="text" placeholder="Ghi chú (VD: Buổi bù)" value={manualSessionForm.note} onChange={e => setManualSessionForm(f => ({ ...f, note: e.target.value }))} style={{ maxWidth: 200 }} />
              <PrimaryButton onClick={handleAddManualSession} style={{ fontSize: '0.83rem' }}>Lưu</PrimaryButton>
              <SecondaryButton onClick={() => setAddingManualSession(false)} style={{ fontSize: '0.83rem' }}>Hủy</SecondaryButton>
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead><tr style={{ background: '#f5f5f5' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Ngày</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Giờ</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Ghi chú</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Trạng thái</th>
              <th style={{ padding: '8px 12px' }}></th>
            </tr></thead>
            <tbody>
              {editingSessions.sort((a, b) => a.sessionDate > b.sessionDate ? 1 : -1).map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #eee', background: s.isActive ? 'white' : '#fff8e1' }}>
                  <td style={{ padding: '7px 12px' }}>
                    {new Date(s.sessionDate).toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                    {s.source === 'manual' && <Badge bg="info" style={{ marginLeft: 6, fontSize: '0.7rem' }}>Bù</Badge>}
                  </td>
                  <td style={{ padding: '7px 12px' }}>{convertTimeToVietnamese(s.startTime, s.endTime)}</td>
                  <td style={{ padding: '7px 12px', fontSize: '0.82rem', maxWidth: 200 }}>
                    {(() => {
                      const note = s.note || '';
                      if (!note) return <span style={{ color: '#bbb' }}>—</span>;

                      // Re-activated holiday session: ACTIVE_HL:<name>
                      if (s.isActive && note.startsWith('ACTIVE_HL:')) {
                        const name = note.slice(10);
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1565c0', background: '#e3f2fd', borderRadius: 6, padding: '2px 7px', border: '1px solid #90caf9' }}>
                            ✨ Vẫn học (dù là {name})
                          </span>
                        );
                      }

                      // Holiday session inactive: HL:<name> or old "Ngh...: <name>"
                      if (!s.isActive && (note.startsWith('HL:') || note.startsWith('Ngh'))) {
                        const name = note.startsWith('HL:') ? note.slice(3) : note.replace(/^Ngh[^:]*:\s*/, '');
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b71c1c', background: '#fff3e0', borderRadius: 6, padding: '2px 7px', border: '1px solid #ffb74d' }}>
                            🏦 Nghỉ lễ: <strong>{name}</strong>
                          </span>
                        );
                      }

                      // Admin manually disabled: ADMIN_OFF or old "Admin đã bỏ..."
                      if (!s.isActive && (note === 'ADMIN_OFF' || note.includes('Admin'))) {
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#c62828', background: '#fce4ec', borderRadius: 6, padding: '2px 7px', border: '1px solid #ef9a9a' }}>
                            ⛔ Admin đã bỏ buổi này
                          </span>
                        );
                      }

                      // Manual makeup session or other notes
                      return <span style={{ color: '#558b2f' }}>{note}</span>;
                    })()}
                  </td>
                  <td style={{ padding: '7px 12px' }}>
                    <button
                      title={s.isActive ? 'Đang học — click để đổi sang Nghỉ' : 'Đang nghỉ — click để kích hoạt lại buổi học'}
                      onClick={() => handleToggleSession(s)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        border: 'none', borderRadius: 20, cursor: 'pointer',
                        padding: '4px 10px 4px 6px',
                        fontSize: '0.8rem', fontWeight: 600,
                        background: s.isActive ? '#e8f5e9' : '#fbe9e7',
                        color: s.isActive ? '#2e7d32' : '#c62828',
                        boxShadow: s.isActive ? '0 0 0 1.5px #a5d6a7' : '0 0 0 1.5px #ef9a9a',
                        transition: 'all 0.2s',
                      }}
                    >
                      {/* Toggle track */}
                      <span style={{
                        display: 'inline-block', width: 32, height: 18, borderRadius: 9,
                        background: s.isActive ? '#43a047' : '#bdbdbd',
                        position: 'relative', flexShrink: 0,
                        transition: 'background 0.25s',
                      }}>
                        {/* Toggle thumb */}
                        <span style={{
                          position: 'absolute', top: 2,
                          left: s.isActive ? 15 : 2,
                          width: 14, height: 14, borderRadius: '50%',
                          background: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          transition: 'left 0.25s',
                        }} />
                      </span>
                      {s.isActive ? '✅ Học' : '⛔ Nghỉ'}
                    </button>
                  </td>
                  <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                    <FiTrash2 style={{ cursor: 'pointer', color: '#c62828' }} onClick={() => handleDeleteSession(s.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {editingSessions.length === 0 && <p style={{ textAlign: 'center', color: '#aaa', padding: 20 }}>Chưa có buổi học nào được tạo.</p>}
        </Modal.Body>
        <Modal.Footer>
          <SecondaryButton onClick={() => setShowSessionsModal(false)}>Đóng</SecondaryButton>
          {editingSessionsClassId != null && (
            <WarningButton
              onClick={async () => {
                setConfirmingClassId(editingSessionsClassId);
                try {
                  const res = await classMonthlySessionService.reConfirm(editingSessionsClassId, currentMonth, currentYear);
                  alert(res.message || 'Đã xác nhận lại!');
                  await loadConfirmedSessions();
                } catch (e: any) { alert('Lỗi: ' + e?.message || e); }
                finally { setConfirmingClassId(null); }
              }}
              disabled={confirmingClassId === editingSessionsClassId}
              style={{ fontSize: '0.85rem' }}
            >
              {confirmingClassId === editingSessionsClassId ? '⏳ Đang xử lý...' : '🔄 Xác nhận lại tháng'}
            </WarningButton>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── Holiday banner — hiển thị cho mọi user khi có ngày nghỉ lễ ── */}
      {holidays.length > 0 && (
        <div style={{
          background: 'linear-gradient(90deg, #fffde7 0%, #fff9c4 100%)',
          border: '1.5px solid #f9a825',
          borderRadius: 10,
          padding: '10px 18px',
          margin: '8px 0 12px 0',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: '1.1rem', marginRight: 4 }}>🎌</span>
          <span style={{ fontWeight: 700, color: '#e65100', fontSize: '0.88rem', marginRight: 8, whiteSpace: 'nowrap' }}>
            Ngày nghỉ lễ T{currentMonth}/{currentYear}:
          </span>
          {holidays.map(h => (
            <span key={h.id} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: '#fff',
              border: `1.5px solid ${h.endDate ? '#ab47bc' : '#fbc02d'}`,
              borderRadius: 20,
              padding: '3px 12px',
              fontSize: '0.83rem',
              fontWeight: 600,
              color: h.endDate ? '#6a1b9a' : '#795548',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}>
              {h.endDate ? (
                <>{fmtHolidayDate(h.date)} → {fmtHolidayDate(h.endDate)} — {h.name}</>
              ) : (
                <>{fmtHolidayDate(h.date)} — {h.name}</>
              )}
            </span>
          ))}
        </div>
      )}

      {/* ── Calendar View ── */}
      {viewMode === 'calendar' && (
        <div style={{ marginTop: 8 }}>
          <ScheduleCalendarView
            schedulesList={schedulesList}
            classes={classes}
            teachers={teachers}
            userRole={userInfo?.role ?? 1}
            onUpdateSchedule={handleCalendarUpdateSchedule}
          />
        </div>
      )}

      {/* ── Table View ── */}
      <TableContainer style={{ display: viewMode === 'calendar' ? 'none' : undefined }}>
        <StyledTable striped>
          <thead>
            <tr>
              <StyledTableHeader>ID</StyledTableHeader>
              <StyledTableHeader>Lớp</StyledTableHeader>
              <StyledTableHeader>Lịch học</StyledTableHeader>
              <StyledTableHeader>Buổi học T{currentMonth}/{currentYear}</StyledTableHeader>
              <StyledTableHeader>Chỉnh Sửa</StyledTableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedSchedules.map((schedule, index) => {
              const cId = schedule.schedules[0].classId;
              const confirmed = isConfirmed(cId);
              const confirmedCount = getConfirmedCount(cId);
              return (
                <tr key={index}>
                  <td>{cId}</td>
                  <td>
                    <div>
                      {schedule.className}
                      <InfoButton style={{ marginLeft: '10px', padding: '6px 12px', fontSize: '13px' }}
                        onClick={() => {
                          const body = groupSchedules(schedule.schedules).map((g) => {
                            const dayParts = g.days.map((d) => `${g.session} ${convertDayOfWeekToVietnamese(d)}`);
                            const daysStr = joinWithAnd(dayParts);
                            const timeStr = ` (từ ${convertTimeToVietnamese(g.startTime, g.endTime)})`;
                            return `${daysStr}${timeStr}`;
                          }).join('\n');
                          handleCopySchedule(`Lịch học lớp ${schedule.className}:\n${body}`);
                        }}>Copy lịch</InfoButton>
                    </div>
                    {(() => {
                      const { activeCount, pendingCount } = getStudentCounts(cId);
                      return (
                        <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#555' }}>
                          <span style={{ marginRight: 10 }}><FiUser color="#28a745" /> {activeCount}</span>
                          <span><FiClock color="#ff9800" /> {pendingCount}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    {groupSchedules(schedule.schedules).map((g, idx) => {
                      const dayParts = g.days.map((d) => `${g.session} ${convertDayOfWeekToVietnamese(d)}`);
                      const daysStr = joinWithAnd(dayParts);
                      return (<div key={idx}>{`${daysStr} (từ ${convertTimeToVietnamese(g.startTime, g.endTime)})`}</div>);
                    })}
                  </td>
                  <td style={{ minWidth: 260 }}>
                    {/* Hiển thị buổi học: nếu đã xác nhận thì lấy từ DB, chưa xác nhận thì preview từ lịch */}
                    {confirmed ? (() => {
                      const sessions = confirmedMap[cId] || [];
                      const activeSessions = sessions.filter(s => s.isActive);
                      const holidayButActive = activeSessions.filter(s => s.note && s.note.startsWith('Ngh'));
                      const inactiveSessions = sessions.filter(s => !s.isActive);
                      // Format giống getMonthSchedule: "Thứ Hai 7/4"
                      const fmtDate = (dateStr: string) => {
                        const [y, m, d] = dateStr.split('-').map(Number);
                        const jsDay = new Date(y, m - 1, d).getDay(); // 0=CN
                        const vn = ['Chủ Nhật','Thứ Hai','Thứ Ba','Thứ Tư','Thứ Năm','Thứ Sáu','Thứ Bảy'];
                        return `${vn[jsDay]} ${d}/${m}`;
                      };
                      return (
                        <div style={{ fontSize: '0.82rem', color: '#333', marginBottom: 6 }}>
                          <div style={{ color: '#2e7d32', marginBottom: 2 }}>
                            <strong>✅ {activeSessions.length} buổi học:</strong>{' '}
                            {activeSessions
                              .sort((a, b) => a.sessionDate > b.sessionDate ? 1 : -1)
                              .map((s, idx, arr) =>
                                s.note && s.note.startsWith('Ngh')
                                  ? <span key={s.id}><span style={{ background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 4, padding: '1px 4px' }} title={s.note}>{fmtDate(s.sessionDate)} ⚠️</span>{idx < arr.length - 1 ? '; ' : ''}</span>
                                  : <span key={s.id}>{fmtDate(s.sessionDate)}{idx < arr.length - 1 ? '; ' : ''}</span>
                              )}
                          </div>
                          {inactiveSessions.length > 0 && (
                            <div style={{ color: '#c62828', marginTop: 2 }}>
                              <span>⛔ Nghỉ lễ ({inactiveSessions.length}):{' '}</span>
                              {inactiveSessions
                                .sort((a, b) => a.sessionDate > b.sessionDate ? 1 : -1)
                                .map((s, idx, arr) => <span key={s.id} title={s.note}>{fmtDate(s.sessionDate)}{idx < arr.length - 1 ? '; ' : ''}</span>)}
                            </div>
                          )}
                          {holidayButActive.length > 0 && (
                            <div style={{ marginTop: 3, color: '#e65100', fontSize: '0.78rem' }}>
                              ⚠️ {holidayButActive.length} buổi trùng lễ nhưng vẫn học
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: 6 }}>
                        {getMonthSchedule(schedule.schedules, currentMonth, currentYear)}
                      </div>
                    )}
                    {/* Badge trạng thái xác nhận + nút — chỉ admin */}
                    {userInfo?.role === 0 && (
                      confirmed ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Badge bg="success" style={{ fontSize: '0.78rem' }}>
                            <FiCheckCircle /> Đã xác nhận — {confirmedCount} buổi
                          </Badge>
                          <InfoButton style={{ fontSize: '0.75rem', padding: '2px 8px' }} onClick={() => handleOpenSessionsModal(cId, schedule.className)}>
                            <FiEdit2 /> Sửa buổi
                          </InfoButton>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <PrimaryButton style={{ fontSize: '0.78rem', padding: '4px 12px' }} onClick={() => handleConfirmSessions(cId, schedule.className)} disabled={confirmingClassId === cId}>
                            {confirmingClassId === cId ? '⏳ Đang xử lý...' : '✅ Xác nhận tháng'}
                          </PrimaryButton>
                          <InfoButton
                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                            disabled={confirmingClassId === cId}
                            title="Tự động xác nhận tháng rồi mở modal sửa buổi"
                            onClick={async () => {
                              setConfirmingClassId(cId);
                              try {
                                await classMonthlySessionService.confirm(cId, currentMonth, currentYear);
                                await loadConfirmedSessions();
                              } catch { /* ignore — sessions may already exist */ }
                              finally { setConfirmingClassId(null); }
                              await handleOpenSessionsModal(cId, schedule.className);
                            }}
                          >
                            {confirmingClassId === cId ? '⏳...' : <><FiEdit2 /> Sửa buổi</>}
                          </InfoButton>
                        </div>
                      )
                    )}
                    {/* Non-admin: chỉ hiển thị trạng thái */}
                    {userInfo?.role !== 0 && confirmed && (
                      <Badge bg="success" style={{ fontSize: '0.78rem' }}>
                        <FiCheckCircle /> Đã xác nhận — {confirmedCount} buổi
                      </Badge>
                    )}
                  </td>
                  <td>
                    <WarningButton disabled={userInfo.role !== 0} onClick={() => handleOpenEditModal(cId, schedule.className, schedule.schedules as Schedule[])}>Sửa</WarningButton>
                    <DangerButton disabled={userInfo.role !== 0} onClick={() => handleClickDelete(cId, schedule.className)}>Xóa</DangerButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
        {showDeletePopup && deletingClass && (
          <ConfirmationPopup isOpen={showDeletePopup} onClose={() => { setShowDeletePopup(false); setDeletingClass(null); }}
            title="Xoá Lịch Học"
            message={<>Bạn có muốn xoá lịch học lớp <strong>{deletingClass.className}</strong> không?</>}
            onConfirm={handleConfirmDelete}
          />
        )}
      </TableContainer>
      {userInfo?.role == 0 && <p>Total: {sortedSchedules.length}</p>}
    </StyledContainer>
  );
};

export default ScheduleManage;
